// Avatar pipeline: load a user-supplied image, mask to a circle, pixelate,
// and produce a single 96x96 HUD texture. The on-screen player sprite is
// generic — the user's likeness only appears in the HUD top-left.
// Falls back to a generated default avatar if no image / load fails.

import Phaser from 'phaser';
import { brandHex } from '../brand';
import { SPRITE_KEYS } from './sprites';

const DEFAULT_AVATAR_KEY = 'avatar_source_default';
const SOURCE_KEY = 'avatar_source';

export const HUD_AVATAR_KEY = 'avatar_hud';

const drawDefaultAvatar = (scene: Phaser.Scene): void => {
  if (scene.textures.exists(DEFAULT_AVATAR_KEY)) return;
  const g = scene.add.graphics();
  g.setVisible(false);
  // gradient-ish circle with a smiley
  const cx = 48, cy = 48, r = 44;
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      const d = Math.sqrt(x * x + y * y);
      if (d <= r) {
        const t = 1 - d / r;
        const c = t > 0.6 ? brandHex.primaryAlt : brandHex.primary;
        g.fillStyle(c, 1);
        g.fillRect(cx + x, cy + y, 1, 1);
      }
    }
  }
  // simple face
  g.fillStyle(0x000000, 1);
  g.fillRect(cx - 14, cy - 8, 6, 6);
  g.fillRect(cx + 8, cy - 8, 6, 6);
  g.fillRect(cx - 12, cy + 12, 24, 4);
  g.generateTexture(DEFAULT_AVATAR_KEY, 96, 96);
  g.destroy();
};

export const loadAvatarSource = (
  scene: Phaser.Scene,
  url: string | null,
  onComplete: () => void,
): void => {
  drawDefaultAvatar(scene);

  if (!url) {
    if (!scene.textures.exists(SOURCE_KEY)) {
      // alias the default as the source
      scene.textures.addImage(SOURCE_KEY, scene.textures.get(DEFAULT_AVATAR_KEY).getSourceImage() as HTMLImageElement);
    }
    onComplete();
    return;
  }

  const finish = () => onComplete();

  // Try direct load (works if the image is CORS-friendly via the proxy or
  // a CORS-enabled CDN). Phaser sets crossOrigin internally.
  scene.load.image(SOURCE_KEY, url);
  scene.load.once(`filecomplete-image-${SOURCE_KEY}`, finish);
  scene.load.once(`loaderror`, (file: Phaser.Loader.File) => {
    if (file.key !== SOURCE_KEY) return;
    console.warn('[avatar] failed to load supplied image, using default:', url);
    if (!scene.textures.exists(SOURCE_KEY)) {
      scene.textures.addImage(SOURCE_KEY, scene.textures.get(DEFAULT_AVATAR_KEY).getSourceImage() as HTMLImageElement);
    }
    finish();
  });
  scene.load.start();
};

const compositeCircular = (
  scene: Phaser.Scene,
  outKey: string,
  size: number,
  withHelmet: boolean,
): void => {
  if (scene.textures.exists(outKey)) scene.textures.remove(outKey);

  // Render source onto a square canvas, mask to a circle, then output.
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const sourceImg = scene.textures.get(SOURCE_KEY).getSourceImage() as HTMLImageElement | HTMLCanvasElement;
  // contain-fit
  const sw = (sourceImg as HTMLImageElement).width || (sourceImg as HTMLCanvasElement).width;
  const sh = (sourceImg as HTMLImageElement).height || (sourceImg as HTMLCanvasElement).height;
  const scale = Math.min(size / sw, size / sh);
  const dw = sw * scale;
  const dh = sh * scale;
  const dx = (size - dw) / 2;
  const dy = (size - dh) / 2;

  // pixelate by drawing to a small intermediate canvas then back up
  const pxStep = Math.max(1, Math.floor(size / 24));
  const small = document.createElement('canvas');
  small.width = Math.max(1, Math.floor(dw / pxStep));
  small.height = Math.max(1, Math.floor(dh / pxStep));
  const sctx = small.getContext('2d');
  if (sctx) {
    sctx.imageSmoothingEnabled = true;
    sctx.drawImage(sourceImg as CanvasImageSource, 0, 0, small.width, small.height);
  }

  // circular clip
  ctx.save();
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();
  ctx.imageSmoothingEnabled = false;
  if (sctx) {
    ctx.drawImage(small, dx, dy, dw, dh);
  } else {
    ctx.drawImage(sourceImg as CanvasImageSource, dx, dy, dw, dh);
  }
  ctx.restore();

  // ring outline
  ctx.beginPath();
  ctx.lineWidth = Math.max(2, Math.floor(size / 24));
  ctx.strokeStyle = `#${brandHex.primaryAlt.toString(16).padStart(6, '0')}`;
  ctx.arc(size / 2, size / 2, size / 2 - ctx.lineWidth / 2, 0, Math.PI * 2);
  ctx.stroke();

  // optional helmet rim across the top half (only for the in-game head)
  if (withHelmet) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
    ctx.clip();
    const grad = ctx.createLinearGradient(0, 0, 0, size / 2);
    grad.addColorStop(0, `#${brandHex.primary.toString(16).padStart(6, '0')}`);
    grad.addColorStop(1, `rgba(124,58,237,0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size / 2);
    // visor band
    ctx.fillStyle = `rgba(34, 211, 238, 0.45)`;
    ctx.fillRect(0, size * 0.32, size, size * 0.16);
    ctx.restore();
  }

  scene.textures.addCanvas(outKey, canvas);
};

export const buildAvatarTextures = (scene: Phaser.Scene): void => {
  if (!scene.textures.exists(SOURCE_KEY)) {
    drawDefaultAvatar(scene);
    scene.textures.addImage(SOURCE_KEY, scene.textures.get(DEFAULT_AVATAR_KEY).getSourceImage() as HTMLImageElement);
  }
  compositeCircular(scene, HUD_AVATAR_KEY, 96, false);
};

export const SPRITE_KEYS_RE = SPRITE_KEYS;
